import { forwardRef, LegacyRef, useImperativeHandle, useRef } from 'react'

import { Grid, GridItem, Stack, TextArea, TextField, Typography } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { CountrySelector } from './CountrySelector'
import { AddressData } from '../schema'

type AddressFormFieldData = Pick<
  AddressData,
  'countryCode' | 'region1' | 'locality1' | 'postalCode' | 'route1' | 'locality3'
>

export type AddressFormField = keyof AddressFormFieldData | 'shared'

type AddressFormProps = {
  title: string
  value: AddressFormFieldData | null
  error?: Record<AddressFormField, { message: string }>
  warning?: Record<AddressFormField, { message: string }>
  onChange: (field: AddressFormField, value: string) => void
  disabledFields?: AddressFormField[]
  requiredFields?: AddressFormField[]
  'aria-label'?: string
}

export const AddressForm = forwardRef((props: AddressFormProps, ref) => {
  const {
    title,
    value: address,
    error: addressError,
    warning: addressWarning,
    disabledFields = [],
    requiredFields = [],
    ['aria-label']: ariaLabel,
    onChange,
  } = props

  const countrySelectedRef = useRef<HTMLDivElement>(null)
  const streetFieldRef = useRef<HTMLDivElement>(null)

  const { translate, isValidTranslationKey } = useLocalisation()
  const translateErrorMessage = (message?: string) =>
    !message ? undefined : isValidTranslationKey(message) ? translate(message) : message

  const isVietnamAddress = address?.countryCode === 'VN'

  useImperativeHandle(ref, () => {
    return {
      focusCountrySelector() {
        countrySelectedRef.current?.focus()
      },
      focusStreetField() {
        streetFieldRef.current?.focus()
      },
    }
  })

  return (
    <Stack aria-label={ariaLabel} gap={8}>
      <Typography variant="small-bold">{title}</Typography>
      <Stack gap={16}>
        <Grid gap={16} col={4}>
          <GridItem>
            <CountrySelector
              value={address?.countryCode ?? ''}
              onChange={(selectedCountryCode) => onChange('countryCode', selectedCountryCode)}
              label={translate('locations.search_address_form.country')}
              error={
                !!addressError?.countryCode || (!disabledFields?.includes('countryCode') && !!addressError?.shared)
              }
              hint={translateErrorMessage(addressError?.countryCode?.message)}
              inputRef={countrySelectedRef as LegacyRef<HTMLInputElement>}
              required={requiredFields?.includes('countryCode')}
              disabled={disabledFields?.includes('countryCode')}
              warningMessage={addressWarning?.countryCode?.message}
            />
          </GridItem>
          {!isVietnamAddress && (
            <GridItem>
              <TextField
                label={translate('locations.search_address_form.state')}
                value={address?.region1 ?? ''}
                onChange={(e) => onChange('region1', e.target.value)}
                required={requiredFields?.includes('region1')}
                disabled={disabledFields?.includes('region1')}
                state={!!address?.region1 && !!addressError?.shared ? 'negative' : undefined}
              />
            </GridItem>
          )}
          <GridItem>
            <TextField
              label={translate('locations.search_address_form.city')}
              value={address?.locality1 ?? ''}
              onChange={(e) => onChange('locality1', e.target.value)}
              required={requiredFields?.includes('locality1')}
              disabled={disabledFields?.includes('locality1')}
              state={!!address?.locality1 && !!addressError?.shared ? 'negative' : undefined}
            />
          </GridItem>
          {isVietnamAddress && (
            <GridItem>
              <TextField
                label={translate('locations.search_address_form.ward')}
                value={address?.locality3 ?? ''}
                onChange={(e) => onChange('locality3', e.target.value)}
                required={requiredFields?.includes('locality3')}
                disabled={disabledFields?.includes('locality3')}
                state={!!address?.locality3 && !!addressError?.shared ? 'negative' : undefined}
              />
            </GridItem>
          )}
          <GridItem>
            <TextField
              label={translate('locations.search_address_form.postal_code')}
              value={address?.postalCode ?? ''}
              onChange={(e) => onChange('postalCode', e.target.value)}
              required={requiredFields?.includes('postalCode')}
              disabled={disabledFields?.includes('postalCode')}
            />
          </GridItem>
        </Grid>
        <Grid gap={16}>
          <GridItem>
            <Stack gap={4}>
              <TextArea
                label={translate('locations.search_address_form.street_name')}
                value={address?.route1 ?? ''}
                onChange={(e) => onChange('route1', e.target.value)}
                state={addressError?.route1 || (!!address?.route1 && !!addressError?.shared) ? 'negative' : undefined}
                hint={translateErrorMessage(addressError?.route1?.message)}
                ref={streetFieldRef as unknown as LegacyRef<HTMLTextAreaElement>}
                required={requiredFields?.includes('route1')}
                disabled={disabledFields?.includes('route1')}
              />
              {!addressError?.route1?.message && !!addressError?.shared && (
                <Typography variant="small" color="negative">
                  {translateErrorMessage(addressError?.shared?.message)}
                </Typography>
              )}
            </Stack>
          </GridItem>
        </Grid>
      </Stack>
    </Stack>
  )
})
